// --- FINAL, UPGRADED FILE: apps/website/src/stores/ui/taskInspector.slice.ts ---

import type { StateCreator } from 'zustand';
import type { ProjectTask, UUID } from '@dopomogai/types';
import { NEW_TASK_TEMPLATE } from './templates';

export interface TaskInspectorState {
  isOpen: boolean;
  editingTask: ProjectTask | null;
  isNew: boolean;
  context: { projectId: UUID; sprintId?: UUID | null } | null;

  // --- NEW, CRITICAL STATE FOR AUTO-SAVE ---
  /** A record of fields that have been changed but not yet saved. e.g., { title: true, status: true } */
  changes: Partial<ProjectTask>;
  /** The current status of the auto-save mechanism. */
  saveStatus: 'idle' | 'saving' | 'success' | 'error';
}

export interface TaskInspectorSlice {
  taskInspector: TaskInspectorState;
  openTaskInspector: (task: ProjectTask | null, context: { projectId: UUID; sprintId?: UUID | null }) => void;
  closeTaskInspector: () => void;
  updateEditingTask: (data: Partial<ProjectTask>) => void;
  
  // --- NEW ACTIONS FOR AUTO-SAVE ---
  promoteNewTaskToEditing: (createdTask: ProjectTask) => void;
  setSaveStatus: (status: TaskInspectorState['saveStatus']) => void;
  clearChanges: () => void;
}

const initialState: TaskInspectorState = {
    isOpen: false,
    editingTask: null,
    isNew: false,
    context: null,
    changes: {},
    saveStatus: 'idle',
};

export const createTaskInspectorSlice: StateCreator<TaskInspectorSlice, [], [], TaskInspectorSlice> = (set) => ({
  taskInspector: initialState,

  openTaskInspector: (task, context) => set({
    taskInspector: {
      ...initialState, // Reset completely on open
      isOpen: true,
      editingTask: task ? { ...task } : NEW_TASK_TEMPLATE(context),
      isNew: !task,
      context: context,
    }
  }),

  closeTaskInspector: () => set({ taskInspector: initialState }),

  updateEditingTask: (data) => set((state) => {
    if (!state.taskInspector.editingTask) return state;
    return {
      taskInspector: {
        ...state.taskInspector,
        editingTask: { ...state.taskInspector.editingTask, ...data },
        changes: { ...state.taskInspector.changes, ...data },
        saveStatus: 'idle', // Set to idle, ready for the hook to debounce
      }
    };
  }),

  // --- NEW ACTION IMPLEMENTATIONS ---

  setSaveStatus: (status) => set(state => ({
    taskInspector: { ...state.taskInspector, saveStatus: status }
  })),

  clearChanges: () => set(state => ({
    taskInspector: { ...state.taskInspector, changes: {} }
  })),

  promoteNewTaskToEditing: (createdTask) => set(state => ({
      taskInspector: {
          ...state.taskInspector,
          editingTask: createdTask, // The task now has a real ID and version
          isNew: false,             // It's no longer a new task
          saveStatus: 'success',    // Show initial success
          changes: {},              // Clear the changes queue
      }
  }))
});